import { useMemo, memo, useState, useCallback, useRef } from 'react';
import { Paper, Box, Typography, IconButton } from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowDown } from '@mui/icons-material';
import ReactTable from '../../../../components/shared/ReactTable/ReactTable';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import { formatCurrencyUSD } from '../../../../utils/common-methods';
import { FEED_SOURCE_DATA_COLUMNS } from '../../../../constants/tableColumns';
import useStyles from './style';
import { UI_TEXT, FEED_SOURCE_TEXT } from '../../constants';

const USD_FIELD_BY_ACCESSOR = {
  cash: 'cashInUsd',
  credit: 'creditInUsd',
  refund: 'refundInUsd',
  total: 'totalInUsd',
};

const SUBLOCATION_PREVIEW_LIMIT = 5;
const SUBLOCATION_SECTION_LABEL = 'Sublocation-wise breakdown';

const FeedSourceDataPanel = memo(
  ({ data = [], sublocationBreakdown = [], isApplied = false, isApproved = false, isLoading = false }) => {
    const classes = useStyles();
    const [expandedRows, setExpandedRows] = useState({});
    const [expandedSublocationsByFeed, setExpandedSublocationsByFeed] = useState({});
    // Keep a ref in sync so columns useMemo can read latest state without listing
    // expandedRows as a dependency — prevents ReactTable from reinitializing on every toggle.
    const expandedRowsRef = useRef(expandedRows);
    expandedRowsRef.current = expandedRows;

    const toggleExpand = useCallback((feedSource) => {
      setExpandedRows((prev) => {
        const nextExpanded = !prev[feedSource];
        if (!nextExpanded) {
          // Reset sublocation preview state when parent row collapses.
          setExpandedSublocationsByFeed((prevSubloc) => ({ ...prevSubloc, [feedSource]: false }));
        }
        return { ...prev, [feedSource]: nextExpanded };
      });
    }, []);

    const toggleSublocationPreview = useCallback((feedSource) => {
      setExpandedSublocationsByFeed((prev) => ({
        ...prev,
        [feedSource]: !prev[feedSource],
      }));
    }, []);

    // Whether any main row has expandable content (currencies OR sublocation rows)
    const hasAnyExpandableRows = useMemo(() => {
      if (!Array.isArray(data)) return false;
      return data.some((row) => {
        if (Array.isArray(row?.currencies) && row.currencies.length > 0) return true;
        const feedLabelNorm = String(row?.feedSource || '').toLowerCase();
        return (
          Array.isArray(sublocationBreakdown) &&
          sublocationBreakdown.some((subloc) =>
            Object.keys(subloc?.feeds || {}).some((k) => k.toLowerCase() === feedLabelNorm),
          )
        );
      });
    }, [data, sublocationBreakdown]);

    const dataWithTotals = useMemo(() => {
      if (!Array.isArray(data) || data.length === 0) {
        return [];
      }

      // Build lookup: normalised feed label -> [{sublocationName, feedData}]
      const sublocationsByFeedLabel = {};
      if (Array.isArray(sublocationBreakdown) && sublocationBreakdown.length > 0) {
        sublocationBreakdown.forEach((subloc) => {
          Object.keys(subloc?.feeds || {}).forEach((feedKey) => {
            const normKey = feedKey.toLowerCase();
            if (!sublocationsByFeedLabel[normKey]) sublocationsByFeedLabel[normKey] = [];
            sublocationsByFeedLabel[normKey].push({
              sublocationName: String(subloc.sublocationName || '').trim(),
              feedData: subloc.feeds[feedKey] || {},
            });
          });
        });
      }

      const totalCash = data.reduce((sum, row) => sum + (row?.cash || 0), 0);
      const totalCredit = data.reduce((sum, row) => sum + (row?.credit || 0), 0);
      const totalRefund = data.reduce((sum, row) => sum + (row?.refund || 0), 0);
      const totalAmount = data.reduce((sum, row) => sum + (Number(row?.total) || 0), 0);

      const allCurrencies = data.flatMap((row) => row?.currencies || []);
      const totalsCurrencies = Object.values(
        (Array.isArray(allCurrencies) ? allCurrencies : []).reduce((acc, currency) => {
          const key = String(currency?.fromCurrency || 'USD').toUpperCase();
          if (!acc[key]) {
            acc[key] = {
              fromCurrency: key,
              cash: 0,
              credit: 0,
              refund: 0,
              total: 0,
              cashInUsd: 0,
              creditInUsd: 0,
              refundInUsd: 0,
              totalInUsd: 0,
            };
          }

          acc[key].cash += Number(currency?.cash) || 0;
          acc[key].credit += Number(currency?.credit) || 0;
          acc[key].refund += Number(currency?.refund) || 0;
          acc[key].total += Number(currency?.total) || 0;
          acc[key].cashInUsd += Number(currency?.cashInUsd) || 0;
          acc[key].creditInUsd += Number(currency?.creditInUsd) || 0;
          acc[key].refundInUsd += Number(currency?.refundInUsd) || 0;
          acc[key].totalInUsd += Number(currency?.totalInUsd) || 0;
          return acc;
        }, {}),
      );

      const result = [];

      data.forEach((row) => {
        const feedLabelNorm = String(row.feedSource || '').toLowerCase();
        const sublocsForFeed = sublocationsByFeedLabel[feedLabelNorm] || [];
        const hasCurrencies = Array.isArray(row.currencies) && row.currencies.length > 0;
        const hasSublocations = sublocsForFeed.length > 0;

        // Main row — annotate whether it can be expanded
        result.push({ ...row, hasExpandableItems: hasCurrencies || hasSublocations });

        if (expandedRows[row.feedSource]) {
          // 1. Currency sub-rows (existing — preserve CAD/USD display)
          if (hasCurrencies) {
            row.currencies.forEach((currency, index) => {
              const isLastCurrency = index === row.currencies.length - 1;
              result.push({
                ...currency,
                isCurrencyRow: true,
                // Remove divider on last currency row when sublocation rows follow immediately
                isCurrencyGroupContinuation: !isLastCurrency || hasSublocations,
                feedSource: '',
                total: Number(currency?.total) || 0,
                totalInUsd: Number(currency?.totalInUsd) || 0,
                currencies: [],
              });
            });
          }

          // 2. Sublocation sub-rows
          const shouldShowAllSublocations = Boolean(expandedSublocationsByFeed[row.feedSource]);
          const visibleSublocations = shouldShowAllSublocations
            ? sublocsForFeed
            : sublocsForFeed.slice(0, SUBLOCATION_PREVIEW_LIMIT);
          const hiddenCount = Math.max(sublocsForFeed.length - visibleSublocations.length, 0);
          const hasMoreControlRow =
            hiddenCount > 0 || (shouldShowAllSublocations && sublocsForFeed.length > SUBLOCATION_PREVIEW_LIMIT);

          if (hasSublocations) {
            result.push({
              isSublocationHeaderRow: true,
              feedSource: '',
              cash: null,
              credit: null,
              refund: null,
              total: null,
              currencies: [],
            });
          }

          visibleSublocations.forEach((subloc, idx) => {
            const isLastVisibleSublocation = idx === visibleSublocations.length - 1;
            result.push({
              isSublocationRow: true,
              isSublocationGroupContinuation: !isLastVisibleSublocation || hasMoreControlRow,
              sublocationName: String(subloc.sublocationName || '').trim(),
              feedSource: '',
              cash: Number(subloc.feedData?.cash) || 0,
              credit: Number(subloc.feedData?.credit) || 0,
              refund: Number(subloc.feedData?.refund) || 0,
              total: Number(subloc.feedData?.total) || 0,
              cashInUsd: Number(subloc.feedData?.cashInUsd) || 0,
              creditInUsd: Number(subloc.feedData?.creditInUsd) || 0,
              refundInUsd: Number(subloc.feedData?.refundInUsd) || 0,
              totalInUsd: Number(subloc.feedData?.totalInUsd) || 0,
              fromCurrency: String(subloc.feedData?.fromCurrency || 'USD').toUpperCase(),
              currencies: [],
            });
          });

          if (hasMoreControlRow) {
            result.push({
              isSublocationMoreRow: true,
              feedSource: '',
              feedSourceLabel: row.feedSource,
              hiddenCount,
              showAll: shouldShowAllSublocations,
              cash: null,
              credit: null,
              refund: null,
              total: null,
              currencies: [],
            });
          }
        }
      });

      result.push({
        isTotalsRow: true,
        feedSource: UI_TEXT.TOTAL,
        cash: totalCash,
        credit: totalCredit,
        refund: totalRefund,
        total: totalAmount,
        currencies: totalsCurrencies,
        hasExpandableItems: totalsCurrencies.length > 0,
      });

      if (expandedRows[UI_TEXT.TOTAL] && totalsCurrencies.length > 0) {
        totalsCurrencies.forEach((currency, index) => {
          result.push({
            ...currency,
            isCurrencyRow: true,
            isCurrencyGroupContinuation: index < totalsCurrencies.length - 1,
            isTotalsRow: true,
            feedSource: '',
            total: Number(currency?.total) || 0,
            totalInUsd: Number(currency?.totalInUsd) || 0,
            currencies: [],
          });
        });
      }

      return result;
    }, [data, expandedRows, sublocationBreakdown, expandedSublocationsByFeed]);

    const columns = useMemo(
      () =>
        FEED_SOURCE_DATA_COLUMNS.map((col) => ({
          ...col,
          Cell: ({ row, value }) => {
            const {
              isTotalsRow,
              isCurrencyRow,
              isSublocationRow,
              isSublocationHeaderRow,
              isSublocationMoreRow,
              fromCurrency,
              hasExpandableItems,
            } = row.original;
            // Read from ref — columns stay stable across expand/collapse toggles
            const isExpanded = Boolean(expandedRowsRef.current[row.original.feedSource]);
            const textClass = isTotalsRow ? classes.cellTextBold : classes.cellText;

            if (isSublocationHeaderRow) {
              if (col.accessor === 'feedSource') {
                return (
                  <Box className={classes.feedSourceCellContent}>
                    <Typography variant="body2" className={classes.sublocationSectionHeaderText}>
                      {SUBLOCATION_SECTION_LABEL}
                    </Typography>
                  </Box>
                );
              }

              return <Box className={classes.sublocationSectionHeaderCell} />;
            }

            // Sublocation +N more / Show less row
            if (isSublocationMoreRow) {
              if (col.accessor === 'feedSource') {
                const hiddenCount = Number(row.original.hiddenCount) || 0;
                const label = row.original.showAll ? 'Show less' : `+ ${hiddenCount} more`;
                return (
                  <Box className={`${classes.sublocationSubRowLabel} ${classes.sublocationMoreRowCell}`}>
                    <Typography
                      role="button"
                      tabIndex={0}
                      variant="body2"
                      className={classes.sublocationMoreLabel}
                      onClick={() => toggleSublocationPreview(row.original.feedSourceLabel)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          toggleSublocationPreview(row.original.feedSourceLabel);
                        }
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                );
              }

              return <Box className={classes.sublocationMoreRowCell} />;
            }

            // Sublocation sub-row (original currency amount + code)
            if (isSublocationRow) {
              if (col.accessor === 'feedSource') {
                return (
                  <Box className={`${classes.sublocationSubRowLabel} ${classes.sublocationSubRowCell}`}>
                    <Typography variant="body2" className={classes.cellText}>
                      {String(row.original.sublocationName || '').trim() || '\u2014'}
                    </Typography>
                  </Box>
                );
              }
              const rawValBeforeSign = Number(row.original[col.accessor]);
              const rawVal =
                col.accessor === 'refund' && Number.isFinite(rawValBeforeSign) && rawValBeforeSign !== 0
                  ? -Math.abs(rawValBeforeSign)
                  : rawValBeforeSign;
              const code = String(row.original.fromCurrency || 'USD').toUpperCase();
              let displayValue = '\u2014';
              if (Number.isFinite(rawVal)) {
                const formattedAmount = rawVal.toLocaleString('en-US', {
                  minimumFractionDigits: rawVal === 0 ? 2 : Number.isInteger(rawVal) ? 0 : 2,
                  maximumFractionDigits: 2,
                });
                displayValue = `${formattedAmount} ${code}`;
              }

              return (
                <Box className={classes.sublocationSubRowCell}>
                  <Typography variant="body2" className={classes.cellText}>
                    {displayValue}
                  </Typography>
                </Box>
              );
            }

            // Currency sub-row (existing behaviour)
            if (isCurrencyRow) {
              if (col.accessor === 'feedSource') {
                return (
                  <Box className={`${classes.currencySubRowLabel} ${classes.currencySubRowCell}`}>
                    <Typography variant="body2" className={classes.cellText}>
                      {String(fromCurrency || '').toUpperCase() || '\u2014'}
                    </Typography>
                  </Box>
                );
              }

              const isRefundColumn = col.accessor === 'refund';
              const rawValBeforeSign = Number(row.original[col.accessor]);
              const usdValBeforeSign = Number(row.original[USD_FIELD_BY_ACCESSOR[col.accessor]]);
              const rawVal =
                isRefundColumn && Number.isFinite(rawValBeforeSign) && rawValBeforeSign !== 0
                  ? -Math.abs(rawValBeforeSign)
                  : rawValBeforeSign;
              const usdVal =
                isRefundColumn && Number.isFinite(usdValBeforeSign) && usdValBeforeSign !== 0
                  ? -Math.abs(usdValBeforeSign)
                  : usdValBeforeSign;
              let displayValue = '\u2014';
              if (Number.isFinite(rawVal)) {
                const code = String(fromCurrency || 'USD').toUpperCase();
                const formattedAmount = rawVal.toLocaleString('en-US', {
                  minimumFractionDigits: rawVal === 0 ? 2 : Number.isInteger(rawVal) ? 0 : 2,
                  maximumFractionDigits: 2,
                });
                displayValue = `${formattedAmount} ${code}`;
                if (Number.isFinite(usdVal)) {
                  displayValue = `${displayValue} (${formatCurrencyUSD(usdVal)})`;
                }
              }

              return (
                <Box className={classes.currencySubRowCell}>
                  <Typography variant="body2" className={classes.cellText}>
                    {displayValue}
                  </Typography>
                </Box>
              );
            }

            // Main feed / totals row
            if (col.accessor === 'feedSource') {
              return (
                <Box className={classes.feedSourceCellContent}>
                  {hasExpandableItems && (
                    <IconButton
                      size="small"
                      onClick={() => toggleExpand(row.original.feedSource)}
                      className={classes.expandToggleBtn}
                      aria-label={isExpanded ? 'Collapse breakdown' : 'Expand breakdown'}
                    >
                      {isExpanded ? (
                        <KeyboardArrowDown className={classes.expandIcon} />
                      ) : (
                        <KeyboardArrowRight className={classes.expandIcon} />
                      )}
                    </IconButton>
                  )}
                  {!hasExpandableItems && hasAnyExpandableRows && (
                    <Box className={classes.expandIconSlot} aria-hidden="true" />
                  )}
                  <Typography variant="body2" className={textClass}>
                    {value || ''}
                  </Typography>
                </Box>
              );
            }

            const numericValue = Number(value) || 0;
            const displayValue =
              col.accessor === 'refund' && numericValue !== 0 ? -Math.abs(numericValue) : numericValue;

            return (
              <Typography variant="body2" className={textClass}>
                {formatCurrencyUSD(displayValue)}
              </Typography>
            );
          },
        })),
      // expandedRows intentionally omitted — read via expandedRowsRef to keep column
      // objects stable and prevent ReactTable from reinitializing on every toggle.
      [classes, hasAnyExpandableRows, toggleExpand, toggleSublocationPreview],
    );

    const rowClassName = useCallback(
      (row) => {
        const rowData = row?.original || {};

        if (rowData.isSublocationMoreRow) {
          return classes.sublocationMoreRow;
        }

        if (rowData.isSublocationHeaderRow) {
          return classes.sublocationSectionHeaderRow;
        }

        if (rowData.isSublocationRow) {
          return `${classes.sublocationSubRow} ${
            rowData.isSublocationGroupContinuation ? classes.sublocationSubRowNoDivider : ''
          }`.trim();
        }

        if (rowData.isCurrencyRow) {
          return `${classes.currencySubRow} ${
            rowData.isCurrencyGroupContinuation ? classes.currencySubRowNoDivider : ''
          }`.trim();
        }

        const isExpandedParent = rowData.hasExpandableItems && Boolean(expandedRows[rowData.feedSource]);
        return isExpandedParent ? classes.expandedParentRow : '';
      },
      [classes, expandedRows],
    );

    if (isLoading) {
      return (
        <Paper elevation={0} className={classes.feedSourceContainer}>
          <Box className={classes.feedSourceEmptyState}>
            <LoadingSpinner containerHeight={100} />
            <Typography variant="body2" color="text.secondary">
              {FEED_SOURCE_TEXT.LOADING_STATE}
            </Typography>
          </Box>
        </Paper>
      );
    }

    if (!isApplied && !isApproved) {
      return null;
    }

    if (!data || data.length === 0) {
      return (
        <Paper elevation={0} className={classes.feedSourceContainer}>
          <Box className={classes.feedSourceEmptyState}>
            <Typography variant="body2" color="text.secondary">
              {FEED_SOURCE_TEXT.EMPTY_STATE}
            </Typography>
          </Box>
        </Paper>
      );
    }

    return (
      <Paper elevation={0} className={classes.feedSourceContainer}>
        <ReactTable columns={columns} data={dataWithTotals} hideHeader={false} rowClassName={rowClassName} />
      </Paper>
    );
  },
);

FeedSourceDataPanel.displayName = 'FeedSourceDataPanel';

export default FeedSourceDataPanel;
